import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';

// Páginas públicas (carga inmediata — siempre visible)
import { LoginPage } from './pages/LoginPage';

// Páginas admin
const DashboardPage        = lazy(() => import('./pages/DashboardPage'));
const ResellersPage        = lazy(() => import('./pages/ResellersPage'));
const TemplatesPage        = lazy(() => import('./pages/TemplatesPage'));
const XuperPlayAdminPage   = lazy(() => import('./pages/admin/XuperPlayAdminPage'));
const DistribuidoresPage   = lazy(() => import('./pages/admin/DistribuidoresPage'));
const ActivarUsuariosPage  = lazy(() => import('./pages/admin/ActivarUsuariosPage'));
const AdminUsersPage       = lazy(() => import('./pages/admin/AdminUsersPage'));

// Páginas reseller
const ResellerDashboardPage  = lazy(() => import('./pages/reseller/ResellerDashboardPage'));
const ResellerTemplatesPage  = lazy(() => import('./pages/reseller/ResellerTemplatesPage'));
const ResellerSettingsPage   = lazy(() => import('./pages/reseller/ResellerSettingsPage'));

// Páginas distribuidor
const DistribuidorDashboardPage = lazy(() => import('./pages/distribuidor/DistribuidorDashboardPage'));
const DistribuidorResellersPage = lazy(() => import('./pages/distribuidor/DistribuidorResellersPage'));

// Páginas compartidas reseller/distribuidor
const JellyfinUsersPage = lazy(() => import('./pages/reseller/JellyfinUsersPage'));
const WalletPage        = lazy(() => import('./pages/reseller/WalletPage'));
const OneTvPage         = lazy(() => import('./pages/reseller/OneTvPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full py-24 text-white/40 text-sm">
      <svg className="animate-spin mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      Cargando...
    </div>
  );
}

function AppContent() {
  const { user } = useAuth();

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Login */}
          <Route
            path="/login"
            element={
              user && user.status === 'active' ? (
                <Navigate to={
                  user.role === 'admin' ? '/dashboard' :
                  user.role === 'distribuidor' ? '/distribuidor/dashboard' :
                  '/reseller/dashboard'
                } replace />
              ) : (
                <LoginPage />
              )
            }
          />

          {/* Admin routes */}
          <Route element={<ProtectedRoute requiredRole="admin" />}>
            <Route element={<MainLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/admin/activar-usuarios" element={<ActivarUsuariosPage />} />
              <Route path="/admin/distribuidores" element={<DistribuidoresPage />} />
              <Route path="/resellers" element={<ResellersPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/admin/xuper-play" element={<XuperPlayAdminPage />} />
              <Route path="/admin/xui-settings" element={<Navigate to="/admin/xuper-play" replace />} />
              <Route path="/admin/usuarios" element={<AdminUsersPage />} />
            </Route>
          </Route>

          {/* Distribuidor routes */}
          <Route element={<ProtectedRoute requiredRole="distribuidor" />}>
            <Route element={<MainLayout />}>
              <Route path="/distribuidor/dashboard" element={<DistribuidorDashboardPage />} />
              <Route path="/distribuidor/resellers" element={<DistribuidorResellersPage />} />
              <Route path="/distribuidor/clientes/2onamedia" element={<JellyfinUsersPage />} />
              <Route path="/distribuidor/clientes/onetv" element={<OneTvPage />} />
              <Route path="/distribuidor/wallet" element={<WalletPage />} />
              <Route path="/distribuidor/templates" element={<ResellerTemplatesPage />} />
              <Route path="/distribuidor/settings" element={<ResellerSettingsPage />} />
            </Route>
          </Route>

          {/* Reseller routes */}
          <Route element={<ProtectedRoute requiredRole="reseller" />}>
            <Route element={<MainLayout />}>
              <Route path="/reseller/dashboard" element={<ResellerDashboardPage />} />
              <Route path="/reseller/users/2onamedia" element={<JellyfinUsersPage />} />
              <Route path="/reseller/users/onetv" element={<OneTvPage />} />
              <Route path="/reseller/wallet" element={<WalletPage />} />
              <Route path="/reseller/templates" element={<ResellerTemplatesPage />} />
              <Route path="/reseller/settings" element={<ResellerSettingsPage />} />
            </Route>
          </Route>

          {/* Raíz */}
          <Route
            path="/"
            element={
              user ? (
                <Navigate to={
                  user.role === 'admin' ? '/dashboard' :
                  user.role === 'distribuidor' ? '/distribuidor/dashboard' :
                  '/reseller/dashboard'
                } replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* 404 → login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
